/* eslint-disable react/destructuring-assignment */
/* eslint-disable @typescript-eslint/no-use-before-define */
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import {
  InstantSearch,
  ClearRefinements,
  CurrentRefinements,
  HitsPerPage,
  Panel,
  Configure,
  InfiniteHits,
  connectRefinementList,
} from 'react-instantsearch-dom';
import {isEqual} from 'lodash';
import {Button, Modal, Spinner} from 'react-bootstrap';
import {
  NoResults,
} from '../../search/widgets';
import {ALGOLIA_BREEDER_INDEX, BE_STATES} from '../../../utils/constants';
import BreederCard from '../card/breeder-card';
import getSelectDropdown from '../../search/widgets/filters/select-dropdown';
import SetLocation from '../../buttons/set-location';
import useRequestLocation from '../../../hooks/useRequestLocation';

import useURLSync from '../../../hooks/useURLSync';
import {urlToSearchState} from '../../../utils/formatters';
import fetchSearchParams from '../../../utils/fetchSearchParams';
import {LocationParams, SearchState} from '../../search/admin-dashboard-tables/interfaces';
import CustomSortBy from '../../search/widgets/sorts/CustomSortBy';
import DebouncedSearchBox from '../../search/widgets/DebouncedSearchBox';
import {useAlgoliaSearchClientLite} from '../../../utils/hooks/useAlgoliaSearchClient';

interface SortParams extends LocationParams {
  sortBy?: string,
}

const Hit: React.FC = ({hit}: any): ReactElement => {
  return (
    <div className="react-breeder-card">
      <BreederCard breeder={hit} key={hit.objectID} />
    </div>
  );
};

const CustomBreedFilter = connectRefinementList(getSelectDropdown(true, 'Breed', [], '', true));
const CustomStateFilter = connectRefinementList(getSelectDropdown(true, 'State', [], '', true));

const BreederSearch = (props: any) => {
  const containerRef: any = useRef(null);
  const headerRef = useRef(null);

  const {
    searchLocation,
    showRequestLocation,
    useSearchByLocation,
    closeModal,
    locationIsLoading,
    setLocationIsLoading,
    stopAsking,
    isAuto,
    onCompletionCallback,
    toggleSearchByLocation,
  } = useRequestLocation(30000);

  const {
    user,
    searchState: propsSearchState,
    onSearchStateChange,
    createURL,
  } = useURLSync(true);

  const {searchClient} = useAlgoliaSearchClientLite(user?.public_key);

  const getInitialSort = () => {
    const {
      lat,
      lng,
      sortBy,
    }: SortParams = fetchSearchParams();
    if (lat && lng) {
      return 'location';
    }
    if (sortBy) {
      return sortBy;
    }
    return ALGOLIA_BREEDER_INDEX!;
  };

  const getInitialIndexName = (currentSort: string) => {
    const {sortBy}: SortParams = fetchSearchParams();
    if (sortBy) {
      return sortBy;
    }
    if (currentSort) {
      if (currentSort === 'location') {
        return ALGOLIA_BREEDER_INDEX!;
      }
      return currentSort;
    }
    return ALGOLIA_BREEDER_INDEX!;
  };

  const [searchState, setSearchState] = useState(
    propsSearchState || urlToSearchState(window.location),
  );
  // This is the value used to track the form state (not the indexName passed to Algolia)
  const [currentSort, setCurrentSort] = useState(getInitialSort());
  // This is the actual indexName passed to Algolia
  const [indexName, setIndexName] = useState(getInitialIndexName(currentSort));
  const [defaultSortRefinement] = useState<string>(ALGOLIA_BREEDER_INDEX!);

  useEffect(() => {
    const newSearchState: SearchState = {...propsSearchState};

    if (currentSort) {
      if (currentSort === 'location') {
        newSearchState.sortBy = ALGOLIA_BREEDER_INDEX!;
      } else {
        newSearchState.sortBy = currentSort;
      }
      setIndexName(newSearchState.sortBy);
    }
    if (!isEqual(newSearchState, searchState)) {
      setSearchState(newSearchState);
    }
  }, [propsSearchState, currentSort]);

  function customSearchStateChange(newSearchState) {
    if (!isEqual(newSearchState, props.searchState)) {
      onSearchStateChange(newSearchState);
      setSearchState(newSearchState);
    }
  }

  function closeFilters() {
    document.body.classList.remove('filtering');
    containerRef.current.scrollIntoView();
    window.removeEventListener('keyup', onKeyUp);
    window.removeEventListener('click', onClick);
  }

  function onKeyUp(event: any) {
    if (event.key !== 'Escape') {
      return;
    }

    closeFilters();
  }

  function onClick(event: any) {
    if (event.target !== headerRef.current) {
      return;
    }

    closeFilters();
  }

  const customSortByCallback = (value: string) => {
    if (value === 'location') {
      toggleSearchByLocation(true);
    } else {
      toggleSearchByLocation(false);
    }
    setCurrentSort(value);
  };

  // If the user does not set location, sort by default instead
  const handleLocationDenial = () => {
    closeModal();
    setCurrentSort(defaultSortRefinement);
  };

  const onAutoCallback = useCallback(() => {
    if (isAuto) {
      setCurrentSort('location');
    }
  }, [isAuto]);

  const handleSetLocationCompletion = useCallback(() => {
    onCompletionCallback(true);
    onAutoCallback();
  }, [onCompletionCallback, onAutoCallback]);

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={indexName}
      searchState={searchState}
      createURL={createURL}
      onSearchStateChange={(updatedSearchState) => {
        customSearchStateChange(updatedSearchState);
      }}
    >
      <Configure
        facetFilters={[
          'publication_status:published',
          'isSearchableSeller:true',
          BE_STATES.map((state) => `state:${state}`),
        ]}
        attributesToSnippet={['description:2']}
        snippetEllipsisText="…"
        removeWordsIfNoResults="allOptional"
        getRankingInfo={true}
        aroundRadius="all"
        aroundLatLng={useSearchByLocation ? searchLocation : ''}
      />
      <section className="results-container py-5">
        <div className="container-filters">
          <div className="my-3">
            <DebouncedSearchBox
              placeholder="Search Breeders..."
              useNativeStyles
            />
          </div>
          <div className="d-flex flex-column flex-lg-row justify-content-between">
            <div className="d-flex flex-wrap mb-2 mb-lg-0">
              <Panel>
                <CustomBreedFilter limit={1000} attribute="breed" />
              </Panel>
              <Panel>
                <CustomStateFilter attribute="state" />
              </Panel>
              <div className="clear-filters" data-layout="desktop">
                <ClearRefinements
                  className="clear-filters-button"
                  translations={{
                    reset: (
                      <>
                        Clear Filters
                      </>
                    ),
                  }}
                />
              </div>
            </div>
            <div className="d-flex flex-column flex-md-row mb-2 mb-md-0">
              <CustomSortBy
                wrapperClass="container-option my-2 my-md-auto mx-0 mx-md-3"
                onChangeCallback={customSortByCallback}
                selected={currentSort}
                defaultRefinement={defaultSortRefinement}
                items={[
                  {
                    label: 'Sort by name (a-z)',
                    value: defaultSortRefinement,
                  },
                  {
                    label: 'Sort by name (z-a)',
                    value: `${ALGOLIA_BREEDER_INDEX}_name_za`,
                  },
                  {
                    label: 'Sort by location (near-far)',
                    value: 'location',
                  },
                ]}
              />
              <HitsPerPage
                className="container-option my-2 my-md-auto"
                items={[
                  {
                    label: 'Show: 15',
                    value: 15,
                  },
                  {
                    label: 'Show: 30',
                    value: 30,
                  },
                  {
                    label: 'Show: 60',
                    value: 60,
                  },
                ]}
                defaultRefinement={15}
              />
            </div>
          </div>
        </div>
        <CurrentRefinements />
        <main className="search-results-container" ref={containerRef}>
          <section className="container-results">
            <InfiniteHits hitComponent={Hit} />
            <NoResults />
          </section>
        </main>
      </section>

      <Modal
        show={showRequestLocation}
        onHide={handleLocationDenial}
      >
        {
          locationIsLoading && (
            <div className="loading-overlay">
              <Spinner animation="border" variant="primary" />
            </div>
          )
        }
        <Modal.Header closeButton>
          <Modal.Title>Search By Location</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Would you like to search by location?</p>
          <SetLocation
            onInitiateSearch={() => setLocationIsLoading(true)}
            onCompletionCallback={handleSetLocationCompletion}
            showStaticForm
          />
        </Modal.Body>
        <Modal.Footer>
          {isAuto && (
            <Button
              variant="danger"
              onClick={() => stopAsking()}
            >
              Don't Ask Again
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={handleLocationDenial}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </InstantSearch>
  );
};

export default BreederSearch;

if (document.getElementById('react-breeder-search')) {
  ReactDOM.render(<BreederSearch />, document.getElementById('react-breeder-search'));
}
